import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ASSET_TYPES, US_STATES } from "@/lib/format";

export default function NewOpportunityDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    asset_type: "business",
    title: "",
    address: "",
    city: "",
    state: "TX",
    zip_code: "",
    asking_price: "",
    listing_url: "",
    description: "",
    days_on_market: 0,
    price_reductions: 0,
    absentee_owner: false,
    tax_delinquent: false,
    owner_name: "",
    owner_phone: "",
    owner_email: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        asking_price: parseFloat(form.asking_price) || 0,
        days_on_market: parseInt(form.days_on_market) || 0,
        price_reductions: parseInt(form.price_reductions) || 0,
        owner_email: form.owner_email || undefined,
      };
      await api.post("/opportunities", payload);
      toast.success("Opportunity created. AI scored.");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6 overflow-y-auto">
      <div className="ae-card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="new-opp-dialog">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400">New Opportunity</div>
            <h2 className="font-heading text-2xl tracking-tight mt-1">Add to platform</h2>
            <p className="text-xs text-slate-500 mt-1">AI will score on creation.</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/8 rounded-sm" data-testid="close-new-opp">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <L label="Asset type">
              <select data-testid="new-opp-asset" value={form.asset_type} onChange={(e) => setForm({ ...form, asset_type: e.target.value })} className="inp">
                {ASSET_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </L>
            <L label="Source URL (optional)">
              <input data-testid="new-opp-url" value={form.listing_url} onChange={(e) => setForm({ ...form, listing_url: e.target.value })} className="inp" />
            </L>
          </div>
          <L label="Title">
            <input required data-testid="new-opp-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="inp" />
          </L>
          <div className="grid grid-cols-3 gap-3">
            <L label="Address" wide>
              <input data-testid="new-opp-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="inp" />
            </L>
            <L label="City">
              <input required data-testid="new-opp-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="inp" />
            </L>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <L label="State">
              <select data-testid="new-opp-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="inp">
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </L>
            <L label="Zip"><input data-testid="new-opp-zip" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} className="inp" /></L>
            <L label="Asking price">
              <input data-testid="new-opp-price" type="number" value={form.asking_price} onChange={(e) => setForm({ ...form, asking_price: e.target.value })} className="inp font-mono" />
            </L>
          </div>
          <L label="Description (paste listing details for better AI scoring)">
            <textarea data-testid="new-opp-desc" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="inp" />
          </L>
          <div className="grid grid-cols-2 gap-3">
            <L label="Days on market">
              <input data-testid="new-opp-dom" type="number" value={form.days_on_market} onChange={(e) => setForm({ ...form, days_on_market: e.target.value })} className="inp font-mono" />
            </L>
            <L label="Price reductions">
              <input data-testid="new-opp-pr" type="number" value={form.price_reductions} onChange={(e) => setForm({ ...form, price_reductions: e.target.value })} className="inp font-mono" />
            </L>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <L label="Owner name"><input data-testid="new-opp-owner" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="inp" /></L>
            <L label="Owner phone"><input data-testid="new-opp-ophone" value={form.owner_phone} onChange={(e) => setForm({ ...form, owner_phone: e.target.value })} className="inp" /></L>
            <L label="Owner email"><input data-testid="new-opp-oemail" type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} className="inp" /></L>
          </div>
          <div className="flex gap-4 text-xs">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input data-testid="new-opp-absentee" type="checkbox" checked={form.absentee_owner} onChange={(e) => setForm({ ...form, absentee_owner: e.target.checked })} className="accent-emerald-500" />
              Absentee owner
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input data-testid="new-opp-tax" type="checkbox" checked={form.tax_delinquent} onChange={(e) => setForm({ ...form, tax_delinquent: e.target.checked })} className="accent-emerald-500" />
              Tax delinquent
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-white/8">
            <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
            <button type="submit" disabled={busy} data-testid="new-opp-submit" className="btn-primary text-sm">
              {busy ? "Scoring with AI…" : "Create & Score"}
            </button>
          </div>
        </form>

        <style>{`.inp { width: 100%; background: #0A0D12; border: 1px solid rgba(255,255,255,0.1); border-radius: 4px; padding: 0.5rem 0.625rem; font-size: 0.875rem; outline: none; transition: all 200ms; color: #F8FAFC; }
.inp:focus { border-color: rgba(16,185,129,0.6); }`}</style>
      </div>
    </div>
  );
}

function L({ label, children, wide }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">{label}</label>
      {children}
    </div>
  );
}
